import { EventKey } from './enums/event-key';
import { formatCustomEvent, isStatusEvent, } from './utils';
const EVENTS_BATCH_INTERVALL_MS = 500;
const EVENTS_STATUS_INTERVALL_MS = 20 * 1000;
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY_MS = 1000;
const RETRY_BACKOFF_MULTIPLIER = 2;
export class TelemetrySession {
    #createStatusProperties;
    #psid;
    #csid = null;
    #appId;
    #appName;
    #appVersion;
    #publicId;
    #endpoint = null;
    #telemetryDisabled = false;
    #queuedEvents = [];
    #sequence = 0;
    #batchTimeout = null;
    #statusTimeout = null;
    #isClosed = false;
    #sendQueue = Promise.resolve();
    #logger;
    #delay = (ms) => {
        return new Promise((resolve) => {
            setTimeout(resolve, ms);
        });
    };
    #fetchWithRetry = async (endpoint, payload, attempt = 1) => {
        try {
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
        }
        catch (error) {
            if (attempt < MAX_RETRY_ATTEMPTS) {
                const delayMs = RETRY_DELAY_MS * (RETRY_BACKOFF_MULTIPLIER ** (attempt - 1));
                await this.#delay(delayMs);
                await this.#fetchWithRetry(endpoint, payload, attempt + 1);
                return;
            }
            throw error;
        }
    };
    constructor(createStatusProperties, options) {
        this.#createStatusProperties = createStatusProperties;
        this.#psid = options.psid;
        this.#appId = options.appId;
        this.#appName = options.appName;
        this.#appVersion = options.appVersion;
        this.#publicId = options.publicId;
        this.#logger = options.logger;
        this.#restartStatusTimer();
    }
    #stopStatusTimer = () => {
        if (this.#statusTimeout) {
            clearTimeout(this.#statusTimeout);
            this.#statusTimeout = null;
        }
    };
    #restartStatusTimer = () => {
        this.#stopStatusTimer();
        if (this.#isClosed) {
            return;
        }
        this.#statusTimeout = setTimeout(() => {
            // do not send status events if the watch response has not returned yet
            if (this.#csid !== null) {
                this.appendEvent({
                    type: EventKey.STATUS,
                    psid: this.#psid,
                });
            }
            this.#restartStatusTimer();
        }, EVENTS_STATUS_INTERVALL_MS);
    };
    #restartSendEventsTimer = () => {
        if (this.#batchTimeout) {
            clearTimeout(this.#batchTimeout);
        }
        this.#batchTimeout = setTimeout(() => {
            this.#flush();
        }, EVENTS_BATCH_INTERVALL_MS);
    };
    #enqueueData = (data) => {
        if (!this.#endpoint) {
            return Promise.resolve();
        }
        const endpoint = this.#endpoint;
        // Ensure events are sent in order by chaining promises sequentially.
        // Each request waits for the previous one to complete (or fail) before starting.
        // This prevents race conditions where later events could arrive before earlier ones.
        this.#sendQueue = this.#sendQueue.finally(async () => {
            try {
                const events = await data;
                const eventsWithCustom = events.map((event) => {
                    if (event.type === EventKey.CUSTOM) {
                        return formatCustomEvent(event);
                    }
                    return event;
                });
                const payload = {
                    v: 2,
                    events: eventsWithCustom,
                };
                await this.#fetchWithRetry(endpoint, payload);
            }
            catch (error) {
                this.#logger?.warn('Failed to send telemetry events after retries:', error instanceof Error ? error : new Error(String(error)));
            }
        });
        return this.#sendQueue;
    };
    #flush = () => {
        if (!this.#endpoint) {
            return Promise.resolve();
        }
        if (this.#queuedEvents.length === 0) {
            return Promise.resolve();
        }
        const eventsPromise = Promise.all(this.#queuedEvents.slice());
        this.#queuedEvents = [];
        return this.#enqueueData(eventsPromise);
    };
    appendEvent(partialEvent) {
        if (this.#telemetryDisabled || this.#isClosed) {
            return;
        }
        const time = Date.now();
        const seq = this.#sequence;
        const partialEventPromise = typeof partialEvent === 'function'
            ? partialEvent
            : () => Promise.resolve(partialEvent);
        const eventPromise = partialEventPromise().then((_partialEvent) => {
            const event = this.#addEventProperties({
                ..._partialEvent,
                time,
                seq,
            });
            return event;
        });
        this.#queuedEvents.push(eventPromise);
        this.#sequence += 1;
        this.#restartSendEventsTimer();
        eventPromise.then(() => {
            this.#restartSendEventsTimer();
        });
    }
    #addEventProperties = async (payload) => {
        const baseEvent = {
            psid: this.psid,
            csid: this.#csid ?? undefined,
            seq: this.#sequence,
            time: Date.now(),
            app_id: this.#appId,
            app_name: this.#appName,
            app_ver: this.#appVersion,
            public_id: this.#publicId,
        };
        if (payload.type === EventKey.NEW_SESSION) {
            baseEvent.csid = undefined;
        }
        if (isStatusEvent(payload)) {
            const statusProperties = await this.#createStatusProperties();
            const event = {
                ...baseEvent,
                ...statusProperties,
                ...payload,
            };
            this.#restartStatusTimer();
            return event;
        }
        else {
            return {
                ...baseEvent,
                ...payload,
            };
        }
    };
    setEndpoint(endpoint) {
        this.#endpoint = endpoint;
    }
    hasEndpoint() {
        return Boolean(this.#endpoint);
    }
    disableTelemetry() {
        this.#telemetryDisabled = true;
        this.#stopStatusTimer();
    }
    get psid() {
        return this.#psid;
    }
    setCsid(csid) {
        this.#csid = csid;
    }
    get csid() {
        return this.#csid;
    }
    get sequence() {
        return this.#sequence;
    }
    close() {
        if (this.#isClosed) {
            return Promise.resolve();
        }
        this.appendEvent({
            type: EventKey.CLOSED,
            psid: this.#psid,
        });
        this.#isClosed = true;
        this.#stopStatusTimer();
        return this.#flush();
    }
}
